/**
 * 애플리케이션 라우팅 설정 컴포넌트
 * - react-router-dom을 이용해 페이지 간 이동을 관리함
 * - 기본 경로("/") 접근 시 로그인 페이지로 리다이렉트 처리
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import RequestContextSync from '@app/components/RequestContextSync';
import AuthBootstrap from '@app/providers/AuthBootstrap';
import AppProtectedRoute from '@app/routes/AppProtectedRoute';
import AdminProtectedRoute from '@app/routes/AdminProtectedRoute';
import useAuthStore from '@entities/auth/model/useAuthStore';
import ApproverPage from '@pages/approval-box/ApproverPage';
import DrafterPage from '@pages/approval-box/DrafterPage';
import ExecutionPage from '@pages/approval-box/ExecutionPage';
import ReferencePage from '@pages/approval-box/ReferencePage';
import TemporaryArchivePage from '@pages/archive-box/TemporaryArchivePage';
import LoginPage from '@pages/login/LoginPage';
import DocumentWritePage from '@pages/new-document/DocumentWritePage';
import FormSelectionPage from '@pages/new-document/FormSelectionPage';
import ApprovalLineSettingPage from '@pages/settings/ApprovalLineSettingPage';
import NotificationSettingPage from '@pages/settings/NotificationSettingPage';
import PasswordSettingPage from '@pages/settings/PasswordSettingPage';
import SignSettingPage from '@pages/settings/SignSettingPage';
import VacationApprovalPage from '@pages/vacation/VacationApprovalPage';
import VacationRequestPage from '@pages/vacation/VacationRequestPage';
import DocumentsManagementPage from '@pages/admin/documents/DocumentsManagementPage';
import VacationManagementPage from '@pages/admin/vacation/VacationManagementPage';
import EmployeesManagementPage from '@pages/admin/employees/EmployeesManagementPage';
import OrganizationManagementPage from '@pages/admin/organization/OrganizationManagementPage';
import DocumentFormsManagementPage from '@pages/admin/forms/DocumentFormsManagementPage';
import ApprovalLineReferenceManagementPage from '@pages/admin/forms/ApprovalLineReferenceManagementPage';
import InquiryManagementPage from '@pages/admin/inquiry/InquiryManagementPage';
import FormAddEditPage from '@pages/admin/forms/FormAddEditPage';
import ReCaptureDocumentImagePage from '@pages/admin/recapture/ReCaptureDocumentImagePage';

function IndexRedirect() {
  // 기본 랜딩은 항상 로그인 페이지여야 합니다.
  // (로그인 유지가 필요한 경우 /login에서 refresh 쿠키로 자동로그인을 시도합니다.)
  useAuthStore((state) => state.token);

  return <Navigate to="/login" replace />;
}

function AdminIndexRedirect() {
  // /admin 접속 시 /admin/login으로 리다이렉트
  return <Navigate to="/admin/login" replace />;
}

/**
 * Router 컴포넌트
 * - 페이지 경로와 대응되는 컴포넌트를 매핑함
 * - LoginPage: 로그인 화면
 * - ApprovalMainPage: 로그인 성공시 메인 화면
 */
export default function Router() {
  return (
    <BrowserRouter>
      <RequestContextSync />
      <Routes>
        {/* 기본 경로 접근 시 /login 으로 리다이렉트 */}
        <Route path="/" element={<IndexRedirect />} />
        {/* 로그인 페이지 */}
        <Route path="/login" element={<LoginPage />} />
        {/* 관리자 진입 경로 */}
        <Route path="/admin" element={<AdminIndexRedirect />} />
        {/* 관리자 로그인 페이지 */}
        <Route path="/admin/login" element={<LoginPage />} />
        {/* 양식 선택 페이지 (새문서) */}
        <Route
          path="/approval/new-document"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <FormSelectionPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        {/* 문서 작성 페이지 */}
        <Route
          path="/approval/new-document/write"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <DocumentWritePage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        {/* 결재함 관련 페이지 */}
        <Route
          path="/approval/drafter"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <DrafterPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/approval/approver"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <ApproverPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/approval/reference"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <ReferencePage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/approval/execution"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <ExecutionPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        {/* 보관함 관련 페이지 */}
        <Route
          path="/approval/temporary-archive"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <TemporaryArchivePage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        {/* 휴가관리 관련 페이지 */}
        <Route
          path="/approval/vacation/request"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <VacationRequestPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/approval/vacation/approval"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <VacationApprovalPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        {/* 설정 관련 페이지 */}
        <Route
          path="/approval/settings/sign"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <SignSettingPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/approval/settings/password"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <PasswordSettingPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/approval/settings/approval-line"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <ApprovalLineSettingPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/approval/settings/notification"
          element={
            <AuthBootstrap>
              <AppProtectedRoute>
                <NotificationSettingPage />
              </AppProtectedRoute>
            </AuthBootstrap>
          }
        />
        {/* 관리자 페이지 */}
        <Route
          path="/admin/documents"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <DocumentsManagementPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/vacation"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <VacationManagementPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/employees"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <EmployeesManagementPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/organization"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <OrganizationManagementPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/forms/document"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <DocumentFormsManagementPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/forms/document/add"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <FormAddEditPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/forms/document/edit/:docId"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <FormAddEditPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/forms/approval-line-reference"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <ApprovalLineReferenceManagementPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/inquiry"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <InquiryManagementPage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/recapture-document-image"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <ReCaptureDocumentImagePage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
        <Route
          path="/admin/documents/recapture-document-image"
          element={
            <AuthBootstrap>
              <AdminProtectedRoute>
                <ReCaptureDocumentImagePage />
              </AdminProtectedRoute>
            </AuthBootstrap>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
